import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import '../../index.css';
import { bibleService } from '../../services/bible';
import { historyService } from '../../services/history';
import { buildBibleHistoryPath, normalizeBibleHistoryPath } from '../../services/bibleHistoryPaths';
import {
  afterReaderSelectionSettled,
  hasReaderTextSelection,
  isReaderTextSelectionEnabled,
  clearReaderTextSelection,
} from '../../utils/readerTextSelection';
import { getChapterNeighbors } from '../../utils/chapterNeighbors';
import VerseCard from '../../components/bible/VerseCard';
import ParallelVerses from '../../components/bible/ParallelVerses';
import ChapterSelector from '../../components/bible/ChapterSelector';
import HistoryModal from '../../components/bible/HistoryModal';
import HeaderMenu from '../../components/bible/HeaderMenu';
import SettingsModal from '../../components/bible/SettingsModal';
import LanguagePromptModal from '../../components/bible/LanguagePromptModal';
import { rememberTranslationChoice } from '../../utils/localeBootstrap';
import AIInterpretation from '../../components/bible/AIInterpretation';
import BookmarkModal from '../../components/bible/BookmarkModal';
import AddBookmarkDialog from '../../components/bible/AddBookmarkDialog';
import SavedSheet from '../../components/bible/SavedSheet';
import BfUndoToast from '../../components/bible/bf/BfUndoToast';
import LanguageSelector from '../../components/bible/LanguageSelector';
import ContextSearchModal from '../../components/bible/ContextSearchModal';
import {
  BfHeader,
  useBfHeaderState,
  BfReadingProgress,
  BfVerse,
  BfAIInterpretation,
  BfVerseInsight,
  BfParallelVerses,
  BfChapterSelector,
  BfMenuSheet,
  BfReadingSettings,
  BfSmartHighlight,
  BfTranslationSwitcher,
  BfChapterPager,
  BfPageLoader,
  BfInlineLoader,
  useBfFeatures,
  useReadingSize,
  useBfTheme,
  useAmbientMode,
  isChapterSwipeGestureActive,
  BfChapterSwipeViewport,
  markTodayAsRead,
} from '../../components/bible/bf';
import { DEFAULT_STUDY_TAB, isStudyTab } from '../../components/bible/bf/studyTabs';
import BfChapterPreview from '../../components/bible/bf/BfChapterPreview';
import ChatStreamView from './ChatStreamView';
import { bookmarkService, isRibbonBookmark, isSavedBookmark } from '../../services/bookmarks';
import * as verseImageService from '../../services/verseImage';
import { t } from '../../i18n/uiText';
import {
  fetchLanguagesList,
  getLanguageCodeForTranslationId,
  getStoredTranslationOrDefault,
} from '../../utils/bibleLocaleSync';
import { buildPublicVerseUrl } from '../../utils/publicVerseUrl';
import { isWebAuthenticated } from '../../utils/webAuth';
import { AUTH_REQUIRED_EVENT, requestAuthPopup } from '../../utils/authPopup';
import { telegramService } from '../../services/telegram';
import {
  syncTelegramOverlay,
  notifyTelegramBackStackChanged,
  TG_OVERLAY_Z,
} from '../../services/telegramBackStack';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';
import { answersGoToBotChat, openBotChat, sendVerseStudyToBot } from '../../services/botChat';

/** Синхронно с `index.css`: `.chapter-header-web-links` только при min-width: 1024px. */
const CHAPTER_HEADER_INLINE_LINKS_MQ = '(min-width: 1024px)';

/** Видимая зона чтения: под шапкой, над нижним пейджером глав. */
function getReaderVisibleBand() {
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  const rootStyle = getComputedStyle(document.documentElement);
  const safeTop =
    parseFloat(rootStyle.getPropertyValue('--bf-tg-content-top')) ||
    parseFloat(rootStyle.getPropertyValue('--bf-safe-top')) ||
    0;
  const safeBottom = parseFloat(rootStyle.getPropertyValue('--bf-safe-bottom')) || 0;

  const header = document.querySelector('.bf-header');
  const headerBottom = header ? header.getBoundingClientRect().bottom : safeTop + 52;

  const pagerEl = document.querySelector('.bf-chapter-pager');
  const pagerTop = pagerEl
    ? pagerEl.getBoundingClientRect().top
    : vh - (56 + safeBottom);

  return {
    top: Math.round(Math.max(headerBottom, safeTop + 48) + 8),
    bottom: Math.round(Math.max(pagerTop - 8, vh * 0.5)),
  };
}

/**
 * Прокрутка так, чтобы стих и панель кнопок (.bf-verse-toolbar) были в видимой зоне.
 */
function scrollVerseIntoView(verseWrapperEl, behavior = 'auto') {
  if (!verseWrapperEl || typeof window === 'undefined') return;

  const stackEl =
    verseWrapperEl.querySelector?.('.bf-verse-stack') ?? verseWrapperEl;
  const verseEl = stackEl.querySelector?.('.bf-verse') ?? stackEl;
  const toolbarEl = stackEl.querySelector?.('.bf-verse-toolbar');
  const verseRect = verseEl.getBoundingClientRect();
  const toolbarRect = toolbarEl?.getBoundingClientRect();
  const blockTop = verseRect.top;
  const blockBottom = toolbarRect?.bottom ?? verseRect.bottom;

  const { top: visibleTop, bottom: visibleBottom } = getReaderVisibleBand();
  const pad = 10;

  if (blockTop >= visibleTop && blockBottom <= visibleBottom) return;

  let delta = 0;
  if (blockBottom > visibleBottom) {
    delta = blockBottom - visibleBottom + pad;
  } else if (blockTop < visibleTop) {
    delta = blockTop - visibleTop - pad;
  }

  if (delta !== 0) {
    window.scrollBy({ top: delta, left: 0, behavior });
  }
}

export default function ChapterView() {
  const isTelegramWebApp = telegramService.isTelegramWebApp;
  const [chapterHeaderWideLayout, setChapterHeaderWideLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(CHAPTER_HEADER_INLINE_LINKS_MQ).matches : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(CHAPTER_HEADER_INLINE_LINKS_MQ);
    const onChange = () => setChapterHeaderWideLayout(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /** На узком экране ссылки в шапке скрыты — дублируем их в бургер-меню (в Telegram так уже было всегда). */
  const showSearchChatInHeaderMenu = isTelegramWebApp || !chapterHeaderWideLayout;

  const { translation: translationParam, bookId, chapter, verse: verseParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const requireAuthForAi = useCallback(() => {
    if (isWebAuthenticated()) return true;
    const next = `${location.pathname}${location.search}`;
    requestAuthPopup(next, true);
    return false;
  }, [location.pathname, location.search]);
  
  // Нормализуем bookId и chapter (убираем ведущие нули) и редиректим, если нужно
  useEffect(() => {
    if (bookId || chapter) {
      const normalizedBookId = bookId ? parseInt(bookId, 10).toString() : null;
      const normalizedChapter = chapter ? parseInt(chapter, 10).toString() : null;
      
      // Если есть ведущие нули, делаем редирект
      if ((bookId && bookId !== normalizedBookId) || (chapter && chapter !== normalizedChapter)) {
        // Строим нормализованный URL на основе текущего пути
        const pathParts = location.pathname.split('/').filter(p => p);
        const newPathParts = [];
        
        // Первая часть пути - это перевод (translation)
        if (pathParts.length > 0) {
          newPathParts.push(pathParts[0]); // Сохраняем перевод
          if (normalizedBookId) newPathParts.push(normalizedBookId);
          if (normalizedChapter) newPathParts.push(normalizedChapter);
          // Сохраняем остальные части пути (verse, parallel, interpretation)
          const startIndex = 1; // После перевода
          for (let i = startIndex + (bookId ? 1 : 0) + (chapter ? 1 : 0); i < pathParts.length; i++) {
            newPathParts.push(pathParts[i]);
          }
        }
        
        const redirectUrl = '/' + newPathParts.join('/');
        navigate(redirectUrl, { replace: true });
        return;
      }
    }
  }, [bookId, chapter, navigate, location.pathname]);
  
  // Обрабатываем случай, когда verse может быть 'parallel', 'interpretation' или 'image'
  const verse = verseParam && verseParam !== 'parallel' && verseParam !== 'interpretation' && verseParam !== 'image' ? verseParam : null;

  // === BibleFirst redesign (по умолчанию вкл.; выкл.: ?bf=0 или localStorage bf-redesign=0) ===
  const { enabled: bfEnabled } = useBfFeatures();
  const bfHeaderState = useBfHeaderState();
  const bfAmbient = useAmbientMode();
  useReadingSize();
  useBfTheme();
  useEffect(() => {
    if (bfEnabled) markTodayAsRead();
  }, [bfEnabled]);

  useEffect(() => {
    if (!bfEnabled || typeof document === 'undefined') return undefined;
    document.documentElement.setAttribute(
      'data-bf-text-select',
      isReaderTextSelectionEnabled() ? 'on' : 'off'
    );
    if (!isReaderTextSelectionEnabled()) {
      clearReaderTextSelection();
    }
    return () => {
      document.documentElement.removeAttribute('data-bf-text-select');
    };
  }, [bfEnabled]);
  const [bfReadingSettingsOpen, setBfReadingSettingsOpen] = useState(false);
  const [bfMenuOpen, setBfMenuOpen] = useState(false);

  const [verses, setVerses] = useState([]);
  const [bookName, setBookName] = useState('');
  const [totalChapters, setTotalChapters] = useState(0);
  const [loading, setLoading] = useState(true);
  const [parallelVerses, setParallelVerses] = useState([]);
  const [showParallel, setShowParallel] = useState(false);
  const [activeVerse, setActiveVerse] = useState(null);
  const [isLoadingParallel, setIsLoadingParallel] = useState(false);
  const closingModalRef = useRef(false);
  const tgOverlayClosesRef = useRef({});
  const [showChapterSelector, setShowChapterSelector] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuOpenedAtRef = useRef(0);
  const skipNextBurgerClickRef = useRef(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);
  const [showAIInterpretation, setShowAIInterpretation] = useState(false);
  const [aiInitialTab, setAiInitialTab] = useState(DEFAULT_STUDY_TAB);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarkModalAllowMove, setBookmarkModalAllowMove] = useState(false);
  const [showAddBookmark, setShowAddBookmark] = useState(false);
  const [showContextSearch, setShowContextSearch] = useState(false);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [showVerseInsight, setShowVerseInsight] = useState(false);
  const [verseInsightInitialTab, setVerseInsightInitialTab] = useState('explain');
  const [chatInitialView, setChatInitialView] = useState('chat');
  const [chatFocusComposer, setChatFocusComposer] = useState(false);
  const [chatMessagePrefix, setChatMessagePrefix] = useState('');
  const [chatAutoRunExplain, setChatAutoRunExplain] = useState(false);
  const [chatAutoRunMode, setChatAutoRunMode] = useState(null);
  const headerMenuChatOpenTimerRef = useRef(null);
  const [hasBookmark, setHasBookmark] = useState(false);
  /** Номера сохранённых стихов этой главы — отметка у стиха и пункт «Убрать из сохранённого». */
  const [savedVerses, setSavedVerses] = useState(() => new Set());
  const bookmarksRef = useRef([]);
  const [undoToast, setUndoToast] = useState(null);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  const [showVerseImage, setShowVerseImage] = useState(false);
  /** Сервер: telegram_id в списке генерации иллюстраций */
  const [canGenerateVerseImage, setCanGenerateVerseImage] = useState(false);
  const [verseImageUrl, setVerseImageUrl] = useState(null);
  const [loadingVerseImage, setLoadingVerseImage] = useState(false);
  const [verseImageError, setVerseImageError] = useState(null);
  const [verseImageNotInCache, setVerseImageNotInCache] = useState(false);
  const verseRefs = useRef({});
  /** Номер стиха, для которого ждём завершения автоскролла перед показом панели; null = не ждём. */
  const pendingVerseScrollRevealRef = useRef(null);

  const clearChapterVerseUiState = useCallback(() => {
    pendingVerseScrollRevealRef.current = null;
    setActiveVerse(null);
    setVerseMenuOpen(false);
    setVerseContextOpen(false);
    setVerseInlineMoreOpen(false);
  }, []);

  /** Стих в URL относится к текущей книге/главе, а не к предыдущей при свайпе. */
  const isVerseInUrlForCurrentChapter = useCallback(
    (pathname) => {
      const parts = String(pathname || '').split('/').filter(Boolean);
      if (parts.length < 3) return false;
      const versePart = parts[3];
      if (
        !versePart ||
        versePart === 'parallel' ||
        versePart === 'interpretation' ||
        versePart === 'image'
      ) {
        return false;
      }
      return String(parts[1]) === String(bookId) && String(parts[2]) === String(chapter);
    },
    [bookId, chapter]
  );

  const headerMenuNow = useCallback(
    () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now(),
    []
  );

  const HEADER_MENU_BACKDROP_GRACE_MS = 650;
  /** Меню стиха (Разбор / Просто / …) — сразу по тапу. */
  const [verseMenuOpen, setVerseMenuOpen] = useState(false);
  /** Шапка BF + нижняя панель глав — после прокрутки к стиху. */
  const [verseContextOpen, setVerseContextOpen] = useState(false);
  const [verseInlineMoreOpen, setVerseInlineMoreOpen] = useState(false);
  const [verseInlineMoreDirection, setVerseInlineMoreDirection] = useState('up');
  const [verseInlineMoreAlign, setVerseInlineMoreAlign] = useState('right');
  const verseInlineMoreRef = useRef(null);

  useEffect(() => {
    if (verseMenuOpen || verseContextOpen) return;
    setVerseInlineMoreOpen(false);
  }, [verseMenuOpen, verseContextOpen, activeVerse?.verse]);

  useEffect(() => {
    if (!verseInlineMoreOpen) return undefined;
    const onPointerDown = (e) => {
      const ref = verseInlineMoreRef.current;
      if (ref && ref.contains(e.target)) return;
      if (e.target?.closest?.('.bf-popover')) return;
      setVerseInlineMoreOpen(false);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown, false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, false);
    };
  }, [verseInlineMoreOpen]);

  const resolveVerseInlineMoreDirection = useCallback(() => {
    if (typeof window === 'undefined' || !verseInlineMoreRef.current) return 'up';
    const rect = verseInlineMoreRef.current.getBoundingClientRect();
    const estimatedMenuHeight = 164;
    const margin = 12;
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    if (spaceBelow >= estimatedMenuHeight || spaceBelow > spaceAbove) return 'down';
    return 'up';
  }, []);

  const resolveVerseInlineMoreAlign = useCallback(() => {
    if (typeof window === 'undefined' || !verseInlineMoreRef.current) return 'right';
    const rect = verseInlineMoreRef.current.getBoundingClientRect();
    const estimatedMenuWidth = 164;
    const margin = 12;
    const spaceToLeft = rect.right - margin;
    const spaceToRight = window.innerWidth - rect.left - margin;
    if (spaceToLeft < estimatedMenuWidth && spaceToRight >= estimatedMenuWidth) return 'left';
    return 'right';
  }, []);

  useLayoutEffect(() => {
    if (showHeaderMenu) {
      headerMenuOpenedAtRef.current = headerMenuNow();
    }
  }, [showHeaderMenu, headerMenuNow]);

  const handleHeaderMenuBackdropTouchStart = useCallback(
    (e) => {
      if (headerMenuNow() - headerMenuOpenedAtRef.current < HEADER_MENU_BACKDROP_GRACE_MS) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [headerMenuNow]
  );

  const handleHeaderMenuBackdropDismiss = useCallback(
    (e) => {
      if (headerMenuNow() - headerMenuOpenedAtRef.current < HEADER_MENU_BACKDROP_GRACE_MS) {
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }
      setShowHeaderMenu(false);
    },
    [headerMenuNow]
  );

  const anyReadingModalOpen = useMemo(
    () =>
      showChapterSelector ||
      showLanguagePrompt ||
      showSettingsModal ||
      showHistory ||
      showParallel ||
      showAIInterpretation ||
      showBookmarks ||
      showAddBookmark ||
      showContextSearch ||
      showChatPopup ||
      showVerseInsight ||
      showVerseImage,
    [
      showChapterSelector,
      showLanguagePrompt,
      showSettingsModal,
      showHistory,
      showParallel,
      showAIInterpretation,
      showBookmarks,
      showAddBookmark,
      showContextSearch,
      showChatPopup,
      showVerseInsight,
      showVerseImage,
    ]
  );

  useEffect(() => {
    if (!anyReadingModalOpen) return undefined;

    setShowHeaderMenu(false);
    document.body.classList.add('bible-modal-chrome-hidden');
    // Только overflow: position:fixed на body ломает iOS Safari при фокусе в поиске селектора книг/глав
    // (пустая зона над клавиатурой, «уехавший» контент).
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.classList.remove('bible-modal-chrome-hidden');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [anyReadingModalOpen]);

  useEffect(() => {
    const onAuthRequired = () => {
      setShowHeaderMenu(false);
      setShowContextSearch(false);
      setShowChatPopup(false);
      setShowAIInterpretation(false);
      setShowVerseImage(false);
      setShowParallel(false);
      setShowChapterSelector(false);
      setVerseContextOpen(false);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  // Escape для модалки картинки (скролл блокирует общий эффект anyReadingModalOpen)
  useEffect(() => {
    if (!showVerseImage) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeVerseImage();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showVerseImage]);

  useEffect(() => {
    return () => {
      if (headerMenuChatOpenTimerRef.current) {
        clearTimeout(headerMenuChatOpenTimerRef.current);
        headerMenuChatOpenTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem('bibleLanguage')) {
      setShowLanguagePrompt(true);
    }
  }, []);

  const isNavigatingBack = useRef(false);
  const prevBookChapterRef = useRef({ bookId: null, chapter: null });

  // BF translation switcher — crossfade при смене перевода:
  //  - сохраняем scrollY до navigate
  //  - выставляем data-bf-switching="true" → плавный fade-out стихов
  //  - после загрузки новых стихов restore scrollY + fade-in
  const [bfCrossfading, setBfCrossfading] = useState(false);
  const pendingScrollY = useRef(null);
  const [books, setBooks] = useState([]);
  const [translation, setTranslation] = useState(() => {
    // Для страницы главы всегда доверяем переводу из URL.
    // Если translationParam есть в URL - используем его.
    // Иначе пытаемся извлечь из pathname или используем localStorage.
    if (translationParam) return translationParam;
    if (typeof window !== 'undefined') {
      // Пытаемся извлечь перевод из текущего пути
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        const firstPart = pathParts[0];
        // Проверяем, что это не число (bookId) и не служебный путь
        if (firstPart && !firstPart.match(/^\d+$/) && firstPart !== 'settings' && firstPart !== 'tasks') {
          return firstPart;
        }
      }
      return getStoredTranslationOrDefault();
    }
    return getStoredTranslationOrDefault();
  });

  /** Сброс выбранного стиха при свайпе главы (UI + стих в URL текущей главы). */
  const dismissVerseSelectionForSwipe = useCallback(() => {
    pendingVerseScrollRevealRef.current = null;
    setActiveVerse(null);
    setVerseMenuOpen(false);
    setVerseContextOpen(false);
    setVerseInlineMoreOpen(false);
    setShowChatPopup(false);
    setChatMessagePrefix('');
    setChatAutoRunExplain(false);
    setChatAutoRunMode(null);
    setChatInitialView('chat');
    setShowVerseInsight(false);
    setVerseInsightInitialTab('explain');

    if (!verse || !isVerseInUrlForCurrentChapter(location.pathname)) {
      return;
    }

    historyService.markProgrammaticNavigation();
    isNavigatingBack.current = true;
    navigate(`/${translation}/${bookId}/${chapter}`, { replace: true });
  }, [
    verse,
    location.pathname,
    isVerseInUrlForCurrentChapter,
    navigate,
    translation,
    bookId,
    chapter,
  ]);

  /** When adding bookmark from verse menu — verse number; null = chapter bookmark flow */
  const [addBookmarkVerse, setAddBookmarkVerse] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollThreshold = 10;
  const bottomThreshold = 40; // у самого низа — показываем панель
  const minScrollUpDelta = 70; // при скролле вверх показываем только после более заметного сдвига (не так быстро)
  const headerRef = useRef(null);
  const readingContainerRef = useRef(null);
  const chapterSwipeRef = useRef(null);
  const chapterSwipeBusyRef = useRef(false);
  const [neighborChapters, setNeighborChapters] = useState({ prev: null, next: null });
  /** Свайп на соседнюю главу: без полноэкранного лоадера и без сброса стека панелей */
  const chapterSwipeNavRef = useRef(null);
  const [contentPaddingTop, setContentPaddingTop] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function ensurePreferredTranslation() {
      if (typeof window === 'undefined') return;
      const storedLanguage = String(localStorage.getItem('bibleLanguage') || '').toLowerCase();
      if (!storedLanguage || !translation) return;

      const languages = await fetchLanguagesList();
      if (cancelled || !languages?.length) return;

      const translationLanguage = getLanguageCodeForTranslationId(translation, languages);
      if (!translationLanguage || translationLanguage === storedLanguage) return;

      const preferredLanguage = languages.find((lang) => lang.code === storedLanguage);
      if (!preferredLanguage) return;

      const storedTranslation = localStorage.getItem('bibleTranslation');
      const preferredTranslation =
        (storedTranslation &&
          Array.isArray(preferredLanguage.translations) &&
          preferredLanguage.translations.some((item) => item.id === storedTranslation) &&
          storedTranslation) ||
        preferredLanguage.defaultTranslation;

      if (!preferredTranslation || preferredTranslation === translation) return;

      const parts = location.pathname.split('/').filter(Boolean);
      if (parts.length === 0) {
        navigate('/' + preferredTranslation, { replace: true });
        return;
      }

      parts[0] = preferredTranslation;
      navigate('/' + parts.join('/'), { replace: true });
    }

    void ensurePreferredTranslation();
    return () => {
      cancelled = true;
    };
  }, [translation, location.pathname, navigate]);

  useEffect(() => {
    if (chapterSwipeNavRef.current?.fromSwipe || chapterSwipeNavRef.current?.skipScroll) {
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [bookId, chapter]);

  // При смене главы/книги убираем выбор стиха и меню
  useEffect(() => {
    clearChapterVerseUiState();
    setAddBookmarkVerse(null);
    if (!chapterSwipeNavRef.current?.fromSwipe) {
      setNeighborChapters({ prev: null, next: null });
    }
  }, [bookId, chapter, clearChapterVerseUiState]);

  useEffect(() => {
    let mounted = true;
    verseImageService
      .isVerseImageAllowed()
      .then((allowed) => {
        if (mounted) setCanGenerateVerseImage(!!allowed);
      })
      .catch(() => {
        if (mounted) setCanGenerateVerseImage(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Синхронизируем translation state с переводом из URL
  // Это критично, чтобы всегда загружать правильный перевод
  useEffect(() => {
    // Если translationParam есть - всегда используем его (приоритет URL)
    if (translationParam && translationParam !== translation) {
      setTranslation(translationParam);
    } else if (!translationParam) {
      // Если translationParam нет, пытаемся извлечь из pathname
      const pathParts = location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        const firstPart = pathParts[0];
        if (firstPart && !firstPart.match(/^\d+$/) && firstPart !== 'settings' && firstPart !== 'tasks' && firstPart !== translation) {
          setTranslation(firstPart);
        }
      }
    }
  }, [translationParam, location.pathname, translation]);

  useEffect(() => {
    const swipeNav = chapterSwipeNavRef.current;
    const fromSwipe = !!swipeNav?.fromSwipe;
    if (fromSwipe) {
      chapterSwipeNavRef.current = {
        skipScroll: !!swipeNav.skipScroll,
      };
    }

    const prev = prevBookChapterRef.current;
    const onlyTranslationChanged = prev.bookId === bookId && prev.chapter === chapter;
    prevBookChapterRef.current = { bookId, chapter };
    const cached = bibleService.isChapterCached(bookId, chapter, translation);

    if (fromSwipe) {
      const cached = bibleService.getCachedChapterSync(bookId, chapter, translation);
      if (cached?.verses?.length) {
        setVerses(cached.verses);
        if (cached.bookName) setBookName(cached.bookName);
        if (cached.totalChapters) setTotalChapters(cached.totalChapters);
      } else {
        void loadChapter(true);
      }
      return;
    }
    loadChapter(onlyTranslationChanged || cached);
  }, [bookId, chapter, translation]);

  const chapterNeighborsMeta = useMemo(() => {
    if (!bfEnabled || !bookId || !chapter || !totalChapters) {
      return { prev: null, next: null };
    }
    return getChapterNeighbors({
      bookId,
      chapter,
      totalChapters,
      books,
    });
  }, [bfEnabled, bookId, chapter, totalChapters, books]);

  // Предзагрузка соседних глав (быстрее переход по свайпу / пейджеру).
  useEffect(() => {
    if (!bfEnabled || !bookId || !chapter || !translation) {
      setNeighborChapters({ prev: null, next: null });
      return undefined;
    }

    const { prev, next } = chapterNeighborsMeta;

    let cancelled = false;

    if (prev) bibleService.prefetchChapter(prev.bookId, prev.chapter, translation);
    if (next) bibleService.prefetchChapter(next.bookId, next.chapter, translation);

    (async () => {
      const [prevData, nextData] = await Promise.all([
        prev ? bibleService.getChapter(prev.bookId, prev.chapter, translation) : null,
        next ? bibleService.getChapter(next.bookId, next.chapter, translation) : null,
      ]);
      if (cancelled) return;
      setNeighborChapters({
        prev: prevData?.verses?.length ? prevData : null,
        next: nextData?.verses?.length ? nextData : null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [bfEnabled, bookId, chapter, translation, chapterNeighborsMeta]);

  const chapterSwipeKey = `${bookId}:${chapter}:${translation}`;

  useLayoutEffect(() => {
    if (!bfEnabled) return;
    const swipeNav = chapterSwipeNavRef.current;
    if (!swipeNav?.fromSwipe) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
    chapterSwipeRef.current?.settleAfterSwipe?.();
    chapterSwipeBusyRef.current = false;
    chapterSwipeNavRef.current = {
      skipScroll: !!swipeNav.skipScroll,
    };
  }, [bfEnabled, chapterSwipeKey]);

  // Восстановление scroll + завершение crossfade при смене перевода.
  // Срабатывает после того, как пришли новые verses из bibleService.getChapter.
  useEffect(() => {
    if (!bfCrossfading) return;
    if (!verses || verses.length === 0) return;
    const y = pendingScrollY.current;
    if (y != null) {
      pendingScrollY.current = null;
      requestAnimationFrame(() => {
        try {
          window.scrollTo({ top: y, behavior: 'instant' });
        } catch {
          window.scrollTo(0, y);
        }
      });
    }
    const tid = setTimeout(() => setBfCrossfading(false), 60);
    return () => clearTimeout(tid);
  }, [verses, bfCrossfading]);

  const handleBfTranslationChange = useCallback(
    (newTranslationId) => {
      if (!newTranslationId || newTranslationId === translation) return;
      // 1) snapshot scroll
      pendingScrollY.current =
        typeof window !== 'undefined'
          ? window.scrollY || window.pageYOffset || 0
          : 0;
      // 2) persist preference (как у легаси-селектора)
      rememberTranslationChoice(newTranslationId);
      // 3) fade-out
      setBfCrossfading(true);
      // 4) navigate с заменой первого сегмента URL; небольшой delay, чтобы
      //    fade-out успел проиграться до подмены текста.
      setTimeout(() => {
        const parts = location.pathname.split('/').filter(Boolean);
        if (parts.length > 0) {
          parts[0] = newTranslationId;
          navigate('/' + parts.join('/'), { replace: true });
        } else {
          navigate('/' + newTranslationId, { replace: true });
        }
      }, 90);
    },
    [translation, location.pathname, navigate],
  );

  // Обработчик скролла: панель появляется у самого верха, у самого низа и при заметном скролле вверх
  useEffect(() => {
    let ticking = false;
    const minScrollDelta = 8;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDelta = currentScrollY - lastScrollY.current;
          const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;

          // В самом верху — показываем
          if (currentScrollY <= scrollThreshold) {
            setIsHeaderVisible(true);
            lastScrollY.current = currentScrollY;
            ticking = false;
            return;
          }

          // В самом низу — показываем
          if (maxScrollY > 0 && currentScrollY >= maxScrollY - bottomThreshold) {
            setIsHeaderVisible(true);
            lastScrollY.current = currentScrollY;
            ticking = false;
            return;
          }

          // Скролл вверх — показываем только после достаточно большого сдвига (не так быстро)
          if (scrollDelta < 0 && Math.abs(scrollDelta) >= minScrollUpDelta) {
            setIsHeaderVisible(true);
            lastScrollY.current = currentScrollY;
            ticking = false;
            return;
          }

          // Скролл вниз — скрываем
          if (scrollDelta > minScrollDelta && currentScrollY > scrollThreshold) {
            setIsHeaderVisible(false);
            lastScrollY.current = currentScrollY;
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Легаси-шапка fixed — нужен padding-top. BF: .bf-header sticky в потоке, доп. отступ не нужен.
  useEffect(() => {
    if (bfEnabled) {
      setContentPaddingTop(0);
      document.documentElement.style.removeProperty('--bf-reading-pad-top');
      return undefined;
    }
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      setContentPaddingTop(headerRef.current.offsetHeight + 8);
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    const timeoutId = setTimeout(updateHeaderHeight, 100);
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      clearTimeout(timeoutId);
    };
  }, [bfEnabled, bookName, chapter, isHeaderVisible, totalChapters, books.length]);

  useEffect(() => {
    bibleService.getBooks(translation).then(setBooks).catch((error) => {
      console.error('Error loading books list:', error);
    });
  }, [translation]);

  // Закладка-ленточка на этой главе и сохранённые стихи главы
  const refreshBookmarks = useCallback(async () => {
    if (!bookId || !chapter) return;
    try {
      const list = (await bookmarkService.getBookmarks()) || [];
      bookmarksRef.current = list;
      const b = parseInt(bookId, 10);
      const c = parseInt(chapter, 10);
      const here = list.filter((x) => x.bookId === b && x.chapter === c);
      setHasBookmark(here.some(isRibbonBookmark));
      setSavedVerses(new Set(here.filter((x) => isSavedBookmark(x) && x.verse).map((x) => x.verse)));
    } catch (error) {
      console.error('Error checking bookmark:', error);
      setHasBookmark(false);
    }
  }, [bookId, chapter]);

  useEffect(() => {
    setSavedVerses(new Set());
    refreshBookmarks();
  }, [refreshBookmarks]);

  const savedHaptic = () => {
    try {
      window?.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch {
      /* no-op */
    }
  };

  /** Стих (или глава, если стиха нет) в «Сохранённое» и обратно — одним нажатием, с «Отменить». */
  const toggleSaveVerse = async (verseNum) => {
    if (!bookId || !chapter || !requireAuthForAi()) return;
    const b = parseInt(bookId, 10);
    const c = parseInt(chapter, 10);
    const v = verseNum ? parseInt(verseNum, 10) : null;
    const label = v ? `${bookName} ${c}:${v}` : `${bookName} ${c}`;
    const mark = (on) => {
      if (!v) return;
      setSavedVerses((prev) => {
        const next = new Set(prev);
        if (on) next.add(v);
        else next.delete(v);
        return next;
      });
    };
    const existing = bookmarksRef.current.find(
      (x) => isSavedBookmark(x) && x.bookId === b && x.chapter === c && (x.verse ?? null) === v
    );
    try {
      if (existing) {
        mark(false);
        bookmarksRef.current = bookmarksRef.current.filter((x) => x.id !== existing.id);
        await bookmarkService.deleteBookmark(existing.id);
        setUndoToast({
          id: Date.now(),
          text: `${t('savedRemoved')} · ${label}`,
          actionLabel: t('undoAction'),
          onAction: async () => {
            mark(true);
            await bookmarkService.saveVerse(b, c, v, existing.note || null);
            refreshBookmarks();
          },
        });
      } else {
        mark(true);
        const created = await bookmarkService.saveVerse(b, c, v);
        if (created?.id) bookmarksRef.current = [...bookmarksRef.current, created];
        savedHaptic();
        setUndoToast({
          id: Date.now(),
          text: `${t('savedDone')} · ${label}`,
          actionLabel: t('undoAction'),
          onAction: async () => {
            mark(false);
            if (created?.id) await bookmarkService.deleteBookmark(created.id);
            refreshBookmarks();
          },
        });
      }
    } catch (error) {
      console.error('Error saving verse:', error);
      setToast({ type: 'error', text: t('bookmarkAddError') });
    }
    refreshBookmarks();
  };

  /**
   * Значок закладки в заголовке: одна закладка — переносится сюда сразу (с «Отменить»),
   * нет закладок — создаём, несколько — выбираем, какую перенести.
   */
  const handleRibbonTap = async () => {
    const openChooser = () => {
      setBookmarkModalAllowMove(true);
      setAddBookmarkVerse(null);
      setShowBookmarks(true);
    };
    if (!bookId || !chapter || !isWebAuthenticated()) {
      openChooser();
      return;
    }
    let ribbons;
    try {
      ribbons = ((await bookmarkService.getBookmarks()) || []).filter(isRibbonBookmark);
    } catch {
      openChooser();
      return;
    }
    const b = parseInt(bookId, 10);
    const c = parseInt(chapter, 10);
    if (ribbons.length === 0) {
      setAddBookmarkVerse(null);
      setShowAddBookmark(true);
      return;
    }
    const only = ribbons.length === 1 ? ribbons[0] : null;
    if (!only || (only.bookId === b && only.chapter === c)) {
      openChooser();
      return;
    }
    try {
      await bookmarkService.updateBookmark(only.id, b, c, null, only.note || null);
      setHasBookmark(true);
      savedHaptic();
      setUndoToast({
        id: Date.now(),
        text: t('ribbonMovedHere').replace('{name}', only.note || t('bookmarkUntitled')),
        actionLabel: t('undoAction'),
        onAction: async () => {
          await bookmarkService.updateBookmark(only.id, only.bookId, only.chapter, only.verse, only.note || null);
          refreshBookmarks();
        },
      });
    } catch (error) {
      console.error('Error moving bookmark:', error);
      setToast({ type: 'error', text: t('bookmarkMoveError') });
    }
    refreshBookmarks();
  };
  
  // Отслеживаем изменения URL для истории (только после загрузки данных)
  useEffect(() => {
    // Не добавляем в историю, пока данные не загружены
    if (loading || !bookName) {
      return;
    }
    
    const currentPath = location.pathname;

    // Не добавляем в историю, если это программный переход назад / Telegram Back
    if (isNavigatingBack.current || historyService.consumeProgrammaticNavigation()) {
      isNavigatingBack.current = false;
      historyService.syncPositionToPath(currentPath);
      return;
    }
    
    // Не добавляем в историю, если это страница интерпретации или параллельных мест
    if (currentPath.includes('/parallel') || currentPath.includes('/interpretation') || currentPath.includes('/image')) {
      return;
    }
    
    const title = `${bookName} ${chapter}${verse ? `:${verse}` : ''}`;
    const firstSegment = currentPath.split('/').filter(Boolean)[0] || getStoredTranslationOrDefault();
    const versePath = buildBibleHistoryPath({
      translation: firstSegment,
      bookId,
      chapter,
      verse,
    });

    // Проверяем, не является ли текущая позиция в стеке уже этим же местом (канонический URL)
    const history = historyService.getHistory();
    const lastHistoryItem = history[historyService.getPosition()];
    if (
      lastHistoryItem &&
      normalizeBibleHistoryPath(lastHistoryItem.url) === versePath
    ) {
      return;
    }
    
    // Сохраняем в историю только стих (без /parallel или /interpretation)
    historyService.saveHistory(
      versePath, 
      title, 
      bookId ? parseInt(bookId) : null,
      chapter ? parseInt(chapter) : null,
      verse ? parseInt(verse) : null
    );
  }, [location.pathname, bookName, chapter, verse, loading, bookId]);
  
  const loadChapter = async (silent = false) => {
    const useSilent =
      silent || bibleService.isChapterCached(bookId, chapter, translation);
    if (!useSilent) setLoading(true);
    try {
      const data = await bibleService.getChapter(bookId, chapter, translation);
      if (data) {
        setVerses(data.verses);
        setBookName(data.bookName);
        setTotalChapters(data.totalChapters);
      }
    } catch (error) {
      console.error('Error loading chapter:', error);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Прокрутка к стиху; после окончания анимации (или сразу при prefers-reduced-motion) вызывается onSettled.
   * Параллельные тапы / смена стиха: onSettled не вызовется, если к этому моменту сменился pendingVerseScrollRevealRef.
   */
  const scheduleVerseScrollThen = (verseNum, onSettled, { showToolbarBeforeScroll = true } = {}) => {
    const token = verseNum;
    pendingVerseScrollRevealRef.current = token;

    setTimeout(() => {
      const found = verses.find((v) => v.verse === verseNum);
      if (found) setActiveVerse(found);
      if (showToolbarBeforeScroll) setVerseMenuOpen(true);

      const finish = () => {
        if (pendingVerseScrollRevealRef.current !== token) return;
        pendingVerseScrollRevealRef.current = null;
        onSettled?.();
      };

      const runScroll = () => {
        const verseElement = verseRefs.current[verseNum];
        if (!verseElement) {
          finish();
          return;
        }

        const reduced =
          typeof window !== 'undefined' &&
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const scrollBehavior = reduced ? 'auto' : 'smooth';
        scrollVerseIntoView(verseElement, scrollBehavior);

        if (reduced) {
          requestAnimationFrame(() => {
            requestAnimationFrame(finish);
          });
          return;
        }

        let settled = false;
        const once = () => {
          if (settled) return;
          settled = true;
          document.removeEventListener('scrollend', onScrollEnd);
          clearTimeout(fallbackTimer);
          finish();
        };
        const onScrollEnd = () => once();
        const fallbackTimer = setTimeout(once, 700);
        document.addEventListener('scrollend', onScrollEnd, { passive: true });
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(runScroll, 16);
        });
      });
    }, 50);
  };

  /** Тап по стиху: URL → автоскролл и панель в useEffect после скролла; без автозапуска чата */
  const handleUserVerseTap = (verse) => {
    const applyVerseTap = () => {
      if (hasReaderTextSelection()) return;
      applyVerseTapInner(verse);
    };
    afterReaderSelectionSettled(applyVerseTap);
  };

  const applyVerseTapInner = (verse) => {
    if ((verseMenuOpen || verseContextOpen) && activeVerse?.verse === verse?.verse) {
      closeVerseSelection();
      return;
    }
    if (showChatPopup) {
      setShowChatPopup(false);
      setChatMessagePrefix('');
    }
    if (showVerseInsight) {
      setShowVerseInsight(false);
      setVerseInsightInitialTab('explain');
    }
    setVerseInlineMoreOpen(false);
    setActiveVerse(verse);
    setVerseMenuOpen(true);
    setVerseContextOpen(true);
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(12);
      }
    } catch {
      /* ignore */
    }
    navigate(`/${translation}/${bookId}/${chapter}/${verse.verse}`, { replace: true });
  };

  const navigateReadingHistoryBack = useCallback(() => {
    const url = historyService.goBack();
    if (!url) return false;
    isNavigatingBack.current = true;
    navigate(url, { replace: true });
    notifyTelegramBackStackChanged();
    return true;
  }, [navigate]);

  const closeVerseSelection = useCallback(() => {
    pendingVerseScrollRevealRef.current = null;
    setActiveVerse(null);
    setVerseMenuOpen(false);
    setVerseContextOpen(false);
    setVerseInlineMoreOpen(false);

    /* Закрытие панели — только снять стих с URL текущей главы, без goBack():
       иначе уезжаем на предыдущую запись истории (другая книга/глава) и срабатывает лишний скролл. */
    if (!verse || !isVerseInUrlForCurrentChapter(location.pathname)) {
      return;
    }

    historyService.markProgrammaticNavigation();
    isNavigatingBack.current = true;
    navigate(`/${translation}/${bookId}/${chapter}`, { replace: true });
    notifyTelegramBackStackChanged();
  }, [
    navigate,
    translation,
    bookId,
    chapter,
    verse,
    location.pathname,
    isVerseInUrlForCurrentChapter,
  ]);

  useEffect(() => {
    if (verse) return;
    pendingVerseScrollRevealRef.current = null;
    setActiveVerse(null);
    setVerseMenuOpen(false);
    setVerseContextOpen(false);
    setVerseInlineMoreOpen(false);
  }, [verse]);

  useEffect(() => {
    if (!activeVerse || (!verseMenuOpen && !verseContextOpen)) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeVerseSelection();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeVerse, verseMenuOpen, verseContextOpen, closeVerseSelection]);

  const openVerseChatForVerse = useCallback(
    (v, autoRunExplain = true, initialView = 'chat', autoRunMode = null, focusComposer = false) => {
      if (!v) return;
      if (!requireAuthForAi()) return;
      pendingVerseScrollRevealRef.current = null;
      setActiveVerse(v);
      setVerseContextOpen(false);
      setChatFocusComposer(Boolean(focusComposer));
      const verseRef = `${bookName} ${chapter}:${v.verse}`;
      const verseText = v.text?.trim();
      setChatMessagePrefix(verseText ? `${verseRef}\n${verseText}` : verseRef);
      setChatAutoRunExplain(Boolean(autoRunExplain));
      setChatAutoRunMode(autoRunMode);
      setChatInitialView(initialView === 'insight' ? 'insight' : 'chat');
      setShowChatPopup(true);
    },
    [bookName, chapter, requireAuthForAi]
  );

  const [botStudyPending, setBotStudyPending] = useState(null);
  const botStudyPendingRef = useRef(false);

  /** Telegram: вкладка толкования или «Спросить» — ответ печатается в чате с ботом, приложение сворачивается. */
  const askInBotChat = useCallback(
    async (v, tab, fallback) => {
      if (!v || botStudyPendingRef.current) return;
      if (!requireAuthForAi()) return;
      botStudyPendingRef.current = true;
      setBotStudyPending(tab);
      telegramService.hapticFeedback('light');
      const result = await sendVerseStudyToBot({ bookId, chapter, verse: v.verse, tab, translation });
      botStudyPendingRef.current = false;
      setBotStudyPending(null);
      if (result.ok) {
        try {
          window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
        } catch {
          /* старый клиент */
        }
        setUndoToast({
          id: Date.now(),
          text: t('botChatSent'),
          actionLabel: t('botChatOpen'),
          onAction: openBotChat,
        });
        openBotChat();
        return;
      }
      if (result.reason === 'limit') {
        setToast({ type: 'error', text: resolveAiLimitMessage(result.payload, t, 'interpretation') });
        return;
      }
      fallback();
    },
    [bookId, chapter, translation, requireAuthForAi]
  );

  const openVerseInsightSheet = useCallback(
    (initialTab = 'explain') => {
      if (!activeVerse) return;
      if (!requireAuthForAi()) return;
      pendingVerseScrollRevealRef.current = null;
      setVerseInsightInitialTab(initialTab);
      setVerseInlineMoreOpen(false);
      setVerseContextOpen(false);
      setShowVerseInsight(true);
    },
    [activeVerse, requireAuthForAi]
  );

  const closeVerseInsight = useCallback(() => {
    setShowVerseInsight(false);
    setVerseInsightInitialTab('explain');
    setVerseInlineMoreOpen(false);
    if (activeVerse) {
      setVerseContextOpen(true);
    }
    notifyTelegramBackStackChanged();
  }, [activeVerse]);

  const closeChatPopup = useCallback(() => {
    const hadVerseContext = Boolean(String(chatMessagePrefix || '').trim());
    setShowChatPopup(false);
    setChatMessagePrefix('');
    setChatAutoRunExplain(false);
    setChatAutoRunMode(null);
    setChatInitialView('chat');
    setChatFocusComposer(false);
    setVerseInlineMoreOpen(false);
    if (hadVerseContext) {
      setVerseContextOpen(true);
    }
    notifyTelegramBackStackChanged();
  }, [chatMessagePrefix]);

  // Прокрутка к стиху после загрузки (если указан стих в URL); панель — после скролла; без автозапуска «Разбора»
  useEffect(() => {
    if (!loading && verse && verses.length > 0) {
      if (isChapterSwipeGestureActive()) return;
      const pathname = location.pathname;
      if (!isVerseInUrlForCurrentChapter(pathname)) {
        return;
      }
      const verseNum = parseInt(verse, 10);
      const opensOtherModal =
        pathname.endsWith('/parallel') ||
        pathname.endsWith('/interpretation') ||
        pathname.endsWith('/image');
      const found = verses.find((v) => v.verse === verseNum);
      if (!opensOtherModal && found) {
        setVerseContextOpen(false);
      }
      scheduleVerseScrollThen(verseNum, () => {
        if (opensOtherModal) return;
        if (!found) return;
        setVerseMenuOpen(true);
        setVerseContextOpen(true);
        bfHeaderState.show?.();
      });
    }
  }, [loading, verse, verses, location.pathname, isVerseInUrlForCurrentChapter]);

  const currentBookId = parseInt(bookId, 10);
  const currentChapterNumber = parseInt(chapter, 10);
  const currentBookIndex = books.findIndex((b) => parseInt(b.id, 10) === currentBookId);
  const previousBook = currentBookIndex > 0 ? books[currentBookIndex - 1] : null;
  const nextBook = currentBookIndex !== -1 && currentBookIndex < books.length - 1 ? books[currentBookIndex + 1] : null;
  const chapterWord = currentBookId === 19 ? t('psalmWord') : t('chapterWord');

  const prevNavTarget = (() => {
    if (currentChapterNumber > 1) {
      return {
        url: `/${translation}/${bookId}/${currentChapterNumber - 1}`,
        label: `${chapterWord} ${currentChapterNumber - 1}`,
      };
    }
    if (previousBook) {
      const lastChapter = previousBook.chapters || previousBook.totalChapters || 1;
      return {
        url: `/${translation}/${previousBook.id}/${lastChapter}`,
        label: previousBook.name,
      };
    }
    return null;
  })();

  const nextNavTarget = (() => {
    if (currentChapterNumber < totalChapters) {
      return {
        url: `/${translation}/${bookId}/${currentChapterNumber + 1}`,
        label: `${chapterWord} ${currentChapterNumber + 1}`,
      };
    }
    if (nextBook) {
      return {
        url: `/${translation}/${nextBook.id}/1`,
        label: nextBook.name,
      };
    }
    return null;
  })();

  const showBfChapterPager = useMemo(() => {
    if (!bfEnabled || bookId == null || chapter == null) return false;
    if (bfMenuOpen) return false;
    if (showHeaderMenu) return false;
    if (
      showChapterSelector ||
      showSettingsModal ||
      bfReadingSettingsOpen ||
      showHistory ||
      showParallel ||
      showAIInterpretation ||
      showBookmarks ||
      showAddBookmark ||
      showContextSearch ||
      showChatPopup ||
      showVerseInsight ||
      showVerseImage ||
      showLanguagePrompt
    ) {
      return false;
    }
    return true;
  }, [
    bfEnabled,
    bookId,
    chapter,
    bfMenuOpen,
    showHeaderMenu,
    showChapterSelector,
    showSettingsModal,
    bfReadingSettingsOpen,
    showHistory,
    showParallel,
    showAIInterpretation,
    showBookmarks,
    showAddBookmark,
    showContextSearch,
    showChatPopup,
    showVerseInsight,
    showVerseImage,
    showLanguagePrompt,
  ]);

  const chapterSwipeEnabled = !loading && showBfChapterPager;

  const navigateChapterBySwipe = useCallback(
    (dir) => {
      if (chapterSwipeBusyRef.current) return;
      const target = dir === 'next' ? nextNavTarget : prevNavTarget;
      if (!target) return;

      const applyChapterData = (data) => {
        if (!data?.verses?.length) return false;
        setVerses(data.verses);
        if (data.bookName) setBookName(data.bookName);
        if (data.totalChapters) setTotalChapters(data.totalChapters);
        return true;
      };

      const neighborData = dir === 'next' ? neighborChapters.next : neighborChapters.prev;
      if (!applyChapterData(neighborData)) {
        const parts = target.url.split('/').filter(Boolean);
        const cached = bibleService.getCachedChapterSync(parts[1], parts[2], parts[0]);
        applyChapterData(cached);
      }

      chapterSwipeBusyRef.current = true;
      chapterSwipeNavRef.current = { fromSwipe: true, skipScroll: true };
      dismissVerseSelectionForSwipe();
      navigate(target.url, { replace: true });
    },
    [
      nextNavTarget,
      prevNavTarget,
      neighborChapters,
      navigate,
      dismissVerseSelectionForSwipe,
    ]
  );

  const openParallelPlaces = async () => {
    if (!activeVerse) return;
    
    // Добавляем стих в историю, если последний элемент не этот стих
    const versePath = buildBibleHistoryPath({
      translation,
      bookId,
      chapter,
      verse: activeVerse.verse,
    });
    const history = historyService.getHistory();
    const lastHistoryItem = history[historyService.getPosition()];
    if (
      !lastHistoryItem ||
      normalizeBibleHistoryPath(lastHistoryItem.url) !== versePath
    ) {
      const title = `${bookName} ${chapter}:${activeVerse.verse}`;
      historyService.saveHistory(
        versePath,
        title,
        bookId ? parseInt(bookId) : null,
        chapter ? parseInt(chapter) : null,
        activeVerse.verse
      );
    }
    
    setIsLoadingParallel(true);
    try {
      const parallels = await bibleService.getParallelVerses(bookId, chapter, activeVerse.verse, translation);
      setParallelVerses(parallels);
      setShowParallel(true);
      // Обновляем URL с путем /parallel
      navigate(`/${translation}/${bookId}/${chapter}/${activeVerse.verse}/parallel`, { replace: true });
    } catch (error) {
      console.error('Error loading parallel verses:', error);
    } finally {
      setIsLoadingParallel(false);
    }
  };

  const closeParallelPlaces = () => {
    closingModalRef.current = true;
    setShowParallel(false);
    setVerseContextOpen(false);
    if (activeVerse?.verse) {
      navigate(
        `/${translation}/${bookId}/${chapter}/${activeVerse.verse}`,
        { replace: true }
      );
    } else if (!navigateReadingHistoryBack()) {
      navigate(`/${translation}/${bookId}/${chapter}`, { replace: true });
    }
    setTimeout(() => {
      closingModalRef.current = false;
    }, 450);
  };

  const openAIInterpretation = (tab = DEFAULT_STUDY_TAB) => {
    if (!requireAuthForAi()) return;
    if (!activeVerse) return;
    setAiInitialTab(isStudyTab(tab) ? tab : DEFAULT_STUDY_TAB);
    
    // Добавляем стих в историю, если последний элемент не этот стих
    const versePath = buildBibleHistoryPath({
      translation,
      bookId,
      chapter,
      verse: activeVerse.verse,
    });
    const history = historyService.getHistory();
    const lastHistoryItem = history[historyService.getPosition()];
    if (
      !lastHistoryItem ||
      normalizeBibleHistoryPath(lastHistoryItem.url) !== versePath
    ) {
      const title = `${bookName} ${chapter}:${activeVerse.verse}`;
      historyService.saveHistory(
        versePath,
        title,
        bookId ? parseInt(bookId) : null,
        chapter ? parseInt(chapter) : null,
        activeVerse.verse
      );
    }
    
    setShowAIInterpretation(true);
    // Обновляем URL с путем /interpretation
    navigate(`/${translation}/${bookId}/${chapter}/${activeVerse.verse}/interpretation`, { replace: true });
  };

  const closeAIInterpretation = () => {
    closingModalRef.current = true;
    setShowAIInterpretation(false);
    setVerseContextOpen(false);
    if (activeVerse?.verse) {
      navigate(
        `/${translation}/${bookId}/${chapter}/${activeVerse.verse}`,
        { replace: true }
      );
    } else if (!navigateReadingHistoryBack()) {
      navigate(`/${translation}/${bookId}/${chapter}`, { replace: true });
    }
    setTimeout(() => {
      closingModalRef.current = false;
    }, 450);
  };

  const openVerseImage = async () => {
    if (!activeVerse || !bookId || !chapter) return;
    if (!requireAuthForAi()) return;
    setShowVerseImage(true);
    setLoadingVerseImage(true);
    setVerseImageError(null);
    setVerseImageNotInCache(false);
    try {
      const result = await verseImageService.getVerseImage(bookId, chapter, activeVerse.verse, translation);
      setVerseImageUrl(result.imageUrl || null);
      setVerseImageNotInCache(!!result.notInCache && !result.imageUrl);
      if (typeof result.canGenerate === 'boolean') {
        setCanGenerateVerseImage(result.canGenerate);
      }
    } catch (err) {
      const msg = err?.code === 'DAILY_AI_LIMIT_REACHED'
        ? resolveAiLimitMessage(err, t, 'verse_image')
        : (err.message || t('error'));
      setToast({ type: 'error', text: msg });
      closeVerseImage();
      return;
    } finally {
      setLoadingVerseImage(false);
    }
  };

  const closeVerseImage = () => {
    closingModalRef.current = true;
    setShowVerseImage(false);
    setVerseImageUrl(null);
    setVerseImageError(null);
    setVerseImageNotInCache(false);
    setVerseContextOpen(false);
    if (activeVerse?.verse) {
      navigate(
        `/${translation}/${bookId}/${chapter}/${activeVerse.verse}`,
        { replace: true }
      );
    } else if (!navigateReadingHistoryBack()) {
      setActiveVerse(null);
      navigate(`/${translation}/${bookId}/${chapter}`, { replace: true });
    }
    setTimeout(() => {
      closingModalRef.current = false;
    }, 450);
  };

  const getVerseImagePageUrl = () => {
    if (!activeVerse || !bookId || !chapter) return '';
    return `${window.location.origin}/${translation}/${bookId}/${chapter}/${activeVerse.verse}/image`;
  };

  const handleCopyVerseImageLink = async () => {
    // Если уже загружен URL изображения, копируем именно его.
    const url = verseImageUrl || getVerseImagePageUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ type: 'success', text: t('linkCopied') });
    } catch {
      setToast({ type: 'error', text: t('error') });
    }
  };

  const handleCopyVerseLink = async () => {
    if (!activeVerse) return;

    const isTelegram = telegramService.isTelegramWebApp;
    let link = '';

    if (isTelegram) {
      // Создаем ссылку для Telegram бота с startapp
      // Новый формат: перевод_книга_глава_стих (если не synodal)
      // Старый формат: книга_глава_стих (для обратной совместимости, если synodal)
      const botUsername = 'VerbumBibleBot';
      let startParam;
      if (translation && translation !== 'synodal') {
        // Новый формат с переводом
        startParam = `${translation}_${bookId}_${chapter}_${activeVerse.verse}`;
      } else {
        // Старый формат без перевода (synodal по умолчанию)
        startParam = `${bookId}_${chapter}_${activeVerse.verse}`;
      }
      link = `https://t.me/${botUsername}/app?startapp=${startParam}`;
    } else {
      // Создаем обычную веб-ссылку
      link = `${window.location.origin}/${translation}/${bookId}/${chapter}/${activeVerse.verse}`;
    }

    // В Telegram Mini App используем специальный подход
    if (isTelegram) {
      try {
        // Пробуем использовать Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } else {
          // Если Clipboard API недоступен, показываем ссылку для ручного копирования
          telegramService.showAlert(`${t('linkLabel')}:\n${link}\n\n${t('copyManually')}`);
        }
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback: показываем ссылку для ручного копирования
        telegramService.showAlert(`${t('linkLabel')}:\n${link}\n\n${t('copyManually')}`);
      }
    } else {
      // В обычном браузере
      try {
        await navigator.clipboard.writeText(link);
        setToast(t('linkCopied'));
        setTimeout(() => setToast(null), 2000);
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } catch (err) {
          alert(`${t('linkLabel')}:\n${link}\n\n${t('copyManually')}`);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  const handleShareVerse = async () => {
    if (!activeVerse) return;
    setVerseContextOpen(false);
    const isTelegram = telegramService.isTelegramWebApp;
    const publicUrl = buildPublicVerseUrl(translation, bookId, chapter, activeVerse.verse);
    const refLine = `${bookName} ${chapter}:${activeVerse.verse}`;
    const verseBody = (activeVerse.text || '').trim();

    let telegramDeepLink = '';
    if (isTelegram) {
      const botUsername = 'VerbumBibleBot';
      const startParam =
        translation && translation !== 'synodal'
          ? `${translation}_${bookId}_${chapter}_${activeVerse.verse}`
          : `${bookId}_${chapter}_${activeVerse.verse}`;
      telegramDeepLink = `https://t.me/${botUsername}/app?startapp=${startParam}`;
    }

    const textParts = [refLine];
    if (verseBody) textParts.push(`«${verseBody}»`);
    textParts.push(publicUrl);
    if (telegramDeepLink) textParts.push(telegramDeepLink);
    const shareText = textParts.join('\n\n');

    try {
      if (navigator.share) {
        await navigator.share({
          title: refLine,
          text: shareText,
          url: publicUrl,
        });
      } else {
        await handleCopyVerseLink();
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      await handleCopyVerseLink();
    }
  };

  // Синхронизация URL ↔ модалки (параллели / толкование / иллюстрация). Лист действий стиха — только React-состояние.
  useEffect(() => {
    if (loading || !verses.length) return;

    const pathname = location.pathname;
    const isParallel = pathname.endsWith('/parallel');
    const isInterpretation = pathname.endsWith('/interpretation');
    const isImage = pathname.endsWith('/image');

    if (!isParallel && !isInterpretation && !isImage) {
      if (showParallel) setShowParallel(false);
      if (showAIInterpretation) setShowAIInterpretation(false);
      if (showVerseImage) setShowVerseImage(false);
      return;
    }

    if (closingModalRef.current) return;

    let verseNumber = verse;
    if (isParallel || isInterpretation || isImage) {
      const pathParts = pathname.split('/').filter(Boolean);
      if (pathParts.length >= 5) {
        const versePart = pathParts[3];
        if (versePart && versePart !== 'parallel' && versePart !== 'interpretation' && versePart !== 'image') {
          verseNumber = versePart;
        }
      }
    }

    if (verseNumber && isVerseInUrlForCurrentChapter(pathname) && !activeVerse) {
      const verseNum = parseInt(verseNumber, 10);
      const foundVerse = verses.find((v) => v.verse === verseNum);
      if (foundVerse) setActiveVerse(foundVerse);
    }

    if (isParallel && activeVerse && !showParallel && !isLoadingParallel) {
      openParallelPlaces();
    }

    if (isInterpretation && activeVerse && !showAIInterpretation) {
      if (!isWebAuthenticated()) {
        requestAuthPopup(pathname, true);
        return;
      }
      setShowAIInterpretation(true);
    }

    if (isImage && activeVerse && !showVerseImage && !loadingVerseImage) {
      if (!isWebAuthenticated()) {
        requestAuthPopup(pathname, true);
        return;
      }
      openVerseImage();
    }
  }, [
    loading,
    verses,
    location.pathname,
    verse,
    activeVerse,
    showParallel,
    showAIInterpretation,
    showVerseImage,
    isLoadingParallel,
    loadingVerseImage,
    bookId,
    chapter,
    navigate,
    isVerseInUrlForCurrentChapter,
  ]);

  // При смене перевода в открытых параллелях — только данные; URL уже обновил LanguageSelector
  useEffect(() => {
    if (!showParallel || !activeVerse || !bookId || !chapter) return;
    let cancelled = false;
    setIsLoadingParallel(true);
    bibleService
      .getParallelVerses(bookId, chapter, activeVerse.verse, translation)
      .then((parallels) => {
        if (!cancelled) setParallelVerses(parallels);
      })
      .catch((err) => {
        console.error('Error loading parallel verses:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingParallel(false);
      });
    return () => {
      cancelled = true;
    };
  }, [translation]);

  tgOverlayClosesRef.current = {
    verseSelection: closeVerseSelection,
    headerMenu: () => setShowHeaderMenu(false),
    chapterSelector: () => setShowChapterSelector(false),
    bfReadingSettings: () => setBfReadingSettingsOpen(false),
    bfMenu: () => setBfMenuOpen(false),
    history: () => setShowHistory(false),
    settingsModal: () => setShowSettingsModal(false),
    parallel: closeParallelPlaces,
    aiInterpretation: closeAIInterpretation,
    bookmarks: () => setShowBookmarks(false),
    addBookmark: () => setShowAddBookmark(false),
    contextSearch: () => setShowContextSearch(false),
    chatPopup: closeChatPopup,
    verseInsight: closeVerseInsight,
    verseImage: closeVerseImage,
    languagePrompt: () => setShowLanguagePrompt(false),
  };

  useEffect(() => {
    if (!telegramService.isTelegramWebApp) return undefined;

    const specs = [
      ['headerMenu', showHeaderMenu, TG_OVERLAY_Z.headerMenu],
      ['chapterSelector', showChapterSelector, TG_OVERLAY_Z.chapterSelector],
      ['bfReadingSettings', bfReadingSettingsOpen, TG_OVERLAY_Z.bfReadingSettings],
      ['bfMenu', bfMenuOpen, TG_OVERLAY_Z.bfMenu],
      ['history', showHistory, TG_OVERLAY_Z.history],
      ['settingsModal', showSettingsModal, TG_OVERLAY_Z.settingsModal],
      ['parallel', showParallel, TG_OVERLAY_Z.parallel],
      ['aiInterpretation', showAIInterpretation, TG_OVERLAY_Z.aiInterpretation],
      ['bookmarks', showBookmarks, TG_OVERLAY_Z.bookmarks],
      ['addBookmark', showAddBookmark, TG_OVERLAY_Z.addBookmark],
      ['contextSearch', showContextSearch, TG_OVERLAY_Z.contextSearch],
      ['chatPopup', showChatPopup, TG_OVERLAY_Z.chatPopup],
      ['verseInsight', showVerseInsight, TG_OVERLAY_Z.verseInsight],
      ['verseImage', showVerseImage, TG_OVERLAY_Z.verseImage],
      ['languagePrompt', showLanguagePrompt, TG_OVERLAY_Z.languagePrompt],
    ];

    specs.forEach(([id, open, z]) => {
      const onClose = tgOverlayClosesRef.current[id];
      if (onClose) syncTelegramOverlay(id, open, onClose, z);
    });

    return () => {
      specs.forEach(([id]) => syncTelegramOverlay(id, false, () => {}, 0));
    };
  }, [
    activeVerse,
    verseMenuOpen,
    verseContextOpen,
    showHeaderMenu,
    showChapterSelector,
    bfReadingSettingsOpen,
    bfMenuOpen,
    showHistory,
    showSettingsModal,
    showParallel,
    showAIInterpretation,
    showBookmarks,
    showAddBookmark,
    showContextSearch,
    showChatPopup,
    showVerseInsight,
    showVerseImage,
    showLanguagePrompt,
    closeVerseSelection,
    closeParallelPlaces,
    closeAIInterpretation,
    closeVerseImage,
    closeChatPopup,
    closeVerseInsight,
  ]);

  if (loading) {
    return bfEnabled ? (
      <BfPageLoader label={t('loading')} />
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-reading-paper">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-semibold">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const handleHistoryBack = () => {
    if (!navigateReadingHistoryBack()) {
      navigate(`/${translation}`, { replace: true });
    }
  };

  const handleHistoryForward = () => {
    const url = historyService.goForward();
    if (url) {
      isNavigatingBack.current = true;
      navigate(url, { replace: true });
    }
  };

  const canGoBack = () => {
    return historyService.canGoBack();
  };

  const canGoForward = () => {
    return historyService.canGoForward();
  };

  const canShowInlineVerseActions =
    !showChapterSelector &&
    !showSettingsModal &&
    !showHistory &&
    !showParallel &&
    !showAIInterpretation &&
    !showBookmarks &&
    !showAddBookmark &&
    !showContextSearch &&
    !showChatPopup &&
    !showVerseInsight &&
    !showVerseImage;

  const renderChapterVerses = () =>
    verses.map((verseItem) => (
      <div
        key={verseItem.verse}
        className={
          activeVerse?.verse === verseItem.verse
            ? 'verse-item verse-item--active'
            : 'verse-item'
        }
        ref={(el) => (verseRefs.current[verseItem.verse] = el)}
        id={`verse-${verseItem.verse}`}
      >
        {bfEnabled ? (
          <BfVerse
            verse={verseItem}
            onVerseTap={handleUserVerseTap}
            isActive={activeVerse?.verse === verseItem.verse}
            isSaved={savedVerses.has(verseItem.verse)}
            showInlineActions={
              verseMenuOpen &&
              activeVerse?.verse === verseItem.verse &&
              canShowInlineVerseActions
            }
            onOpenStudy={(tab) => {
              const openPanel = () => {
                setVerseContextOpen(false);
                openAIInterpretation(tab);
              };
              if (answersGoToBotChat()) {
                void askInBotChat(activeVerse ?? verseItem, tab, openPanel);
                return;
              }
              openPanel();
            }}
            onOpenParallels={() => {
              setVerseContextOpen(false);
              openParallelPlaces();
            }}
            onShare={handleShareVerse}
            onBookmark={() => toggleSaveVerse(activeVerse?.verse ?? verseItem.verse)}
            isLoadingParallel={isLoadingParallel}
            pendingStudyTab={botStudyPending}
            onClose={closeVerseSelection}
          />
        ) : (
          <VerseCard
            verse={verseItem}
            onVerseTap={handleUserVerseTap}
            isActive={activeVerse?.verse === verseItem.verse}
            showInlineActions={
              verseMenuOpen &&
              activeVerse?.verse === verseItem.verse &&
              canShowInlineVerseActions
            }
            onOpenInsight={() => {
              setVerseInlineMoreOpen(false);
              openVerseInsightSheet();
            }}
            onOpenKeyIdea={() => {
              if (!activeVerse) return;
              setVerseInlineMoreOpen(false);
              openVerseChatForVerse(activeVerse, false, 'insight', 'keyIdea');
            }}
            moreRef={verseInlineMoreRef}
            moreOpen={verseInlineMoreOpen}
            onToggleMore={() => {
              setVerseInlineMoreOpen((v) => {
                if (!v) {
                  setVerseInlineMoreDirection(resolveVerseInlineMoreDirection());
                  setVerseInlineMoreAlign(resolveVerseInlineMoreAlign());
                }
                return !v;
              });
            }}
            moreDirection={verseInlineMoreDirection}
            moreAlign={verseInlineMoreAlign}
            onOpenInterpretation={() => {
              setVerseInlineMoreOpen(false);
              setVerseContextOpen(false);
              openAIInterpretation();
            }}
            onOpenParallels={() => {
              setVerseInlineMoreOpen(false);
              setVerseContextOpen(false);
              openParallelPlaces();
            }}
            onShare={() => {
              setVerseInlineMoreOpen(false);
              handleShareVerse();
            }}
            isLoadingParallel={isLoadingParallel}
          />
        )}
      </div>
    ));

  return (
    <div>
      {toast && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-[9999] animate-slide-up max-w-[90vw]">
          <div className={`flex items-center gap-2 text-white pl-4 pr-10 py-3 rounded-xl shadow-lg font-medium relative ${typeof toast === 'object' && toast?.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
            <span className="min-w-0 truncate">{typeof toast === 'object' && toast !== null ? toast.text : toast}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 text-white font-bold text-lg leading-none"
              aria-label={t('close')}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div
        className={`content-with-nav content-with-nav-reading ${bfEnabled ? 'bf-app bf-app--chapter' : ''}`}
        data-bf={bfEnabled ? 'true' : 'false'}
        data-bf-pager={bfEnabled && showBfChapterPager ? 'true' : 'false'}
        {...(bfEnabled ? bfAmbient.getContainerProps() : {})}
      >
      {bfEnabled && (
        <>
          <BfHeader
            bookName={bookName}
            chapter={chapter}
            hidden={bfHeaderState.hidden || bfAmbient.ambient}
            scrolled={bfHeaderState.scrolled}
            onOpenChapterSelector={() => setShowChapterSelector(true)}
            onOpenSearch={() => {
              if (!requireAuthForAi()) return;
              setShowContextSearch(true);
            }}
            onOpenBookmarks={handleRibbonTap}
            onOpenMenu={() => setBfMenuOpen(true)}
            hasBookmark={hasBookmark}
            translationSlot={
              <BfTranslationSwitcher
                currentTranslation={translation}
                onChange={handleBfTranslationChange}
              />
            }
          />
          {bfAmbient.ambient && (
            <button
              type="button"
              className="bf-focus-exit"
              onClick={() => bfAmbient.setAmbient(false)}
              aria-label={t('exitFocusMode') || 'Exit focus'}
              title={t('exitFocusMode') || 'Exit focus'}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </>
      )}
      {/* Фиксированный заголовок: пилл + бейдж + поиск + меню (как на референсе) */}
      <div
        ref={headerRef}
        className={`chapter-header-fixed chapter-header-ref-style chapter-header-reading ${
          !showChapterSelector &&
          !showSettingsModal &&
          (isHeaderVisible || showParallel || verseContextOpen)
            ? 'header-visible'
            : 'header-hidden'
        } ${bfEnabled ? 'bf-legacy-hidden' : ''}`}
      >
        <button
          type="button"
          className="chapter-header-pill"
          onClick={() => setShowChapterSelector(true)}
          aria-label={t('chapterSelect')}
        >
          <span className="chapter-header-pill-text">
            <span className="chapter-header-pill-book">{bookName}</span>
            <span className="chapter-header-pill-num">{chapter}</span>
          </span>
        </button>
        <LanguageSelector currentTranslation={translation} />
        <span className="chapter-header-spacer" aria-hidden="true" />
        <div className="chapter-header-web-links" aria-label={t('menu')}>
          <button
            type="button"
            className="chapter-header-web-link"
            onClick={() => {
              if (!requireAuthForAi()) return;
              setShowContextSearch(true);
              setShowHeaderMenu(false);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            {t('search')}
          </button>
          <button
            type="button"
            className="chapter-header-web-link"
            onClick={() => {
              if (!requireAuthForAi()) return;
              setChatMessagePrefix('');
              setChatAutoRunExplain(false);
              setShowChatPopup(true);
              setShowHeaderMenu(false);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            {t('chatActionLabel')}
          </button>
        </div>
        <button
          type="button"
          className={`chapter-header-bookmark-button ${hasBookmark ? 'has-bookmark' : ''}`}
          onClick={handleRibbonTap}
          aria-label={t('bookmarks')}
          title={t('bookmarks')}
        >
          <svg className="chapter-header-bookmark-icon" viewBox="0 0 24 24" fill={hasBookmark ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
        <div className="chapter-header-burger-wrapper">
          <button
            type="button"
            className="chapter-header-burger-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowHeaderMenu((prev) => !prev);
            }}
            aria-label={t('menu')}
          >
            <svg className="chapter-header-burger-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          {showHeaderMenu && (
            <HeaderMenu
              isOpen={showHeaderMenu}
              showSearchAndChat={showSearchChatInHeaderMenu}
              onSelectChapter={() => {
                setShowHeaderMenu(false);
                setShowChapterSelector(true);
              }}
              onShowHistory={() => {
                setShowHeaderMenu(false);
                setShowHistory(true);
              }}
              onShowBookmarks={() => {
                setShowHeaderMenu(false);
                setBookmarkModalAllowMove(false);
                setShowBookmarks(true);
              }}
              onOpenSearch={() => {
                setShowHeaderMenu(false);
                if (!requireAuthForAi()) return;
                setShowContextSearch(true);
              }}
              onOpenChat={() => {
                setShowHeaderMenu(false);
                if (!requireAuthForAi()) return;
                setChatMessagePrefix('');
                setChatAutoRunExplain(false);
                if (headerMenuChatOpenTimerRef.current) {
                  clearTimeout(headerMenuChatOpenTimerRef.current);
                }
                // В Telegram WebView одновременные анимации закрытия меню и открытия чата
                // визуально выглядят как двойной "слайд". Открываем чат на следующем такте.
                headerMenuChatOpenTimerRef.current = setTimeout(() => {
                  setShowChatPopup(true);
                  headerMenuChatOpenTimerRef.current = null;
                }, 140);
              }}
              onGoSettings={() => {
                setShowHeaderMenu(false);
                setShowSettingsModal(true);
              }}
              onGoHome={() => {
                setShowHeaderMenu(false);
                navigate(`/${translation}`);
              }}
              onClose={() => setShowHeaderMenu(false)}
            />
          )}
        </div>
      </div>

      {showHeaderMenu && (
        <div
          className="header-menu-backdrop"
          aria-hidden="true"
          onTouchStart={handleHeaderMenuBackdropTouchStart}
          onTouchEnd={handleHeaderMenuBackdropDismiss}
          onClick={handleHeaderMenuBackdropDismiss}
        />
      )}

      <div
        ref={readingContainerRef}
        className="reading-container reading-container-books reading-container-with-header reading-container-with-bottom-nav"
        style={
          !bfEnabled && contentPaddingTop > 0
            ? { paddingTop: `${contentPaddingTop}px` }
            : undefined
        }
      >
        {bfEnabled ? (
          <BfChapterSwipeViewport
            ref={chapterSwipeRef}
            enabled={chapterSwipeEnabled}
            chapterKey={chapterSwipeKey}
            allowPrev={Boolean(prevNavTarget)}
            allowNext={Boolean(nextNavTarget)}
            onSwipeEngaged={dismissVerseSelectionForSwipe}
            onNavigatePrev={prevNavTarget ? () => navigateChapterBySwipe('prev') : undefined}
            onNavigateNext={nextNavTarget ? () => navigateChapterBySwipe('next') : undefined}
            prevPanel={
              chapterNeighborsMeta.prev ? (
                neighborChapters.prev?.verses?.length ? (
                  <BfChapterPreview verses={neighborChapters.prev.verses} />
                ) : (
                  <div className="bf-chapter-swipe-panel__loading" aria-hidden="true" />
                )
              ) : (
                <div className="bf-chapter-swipe-panel__empty" aria-hidden="true" />
              )
            }
            currentPanel={
              <div data-bf-switching={bfCrossfading ? 'true' : 'false'}>
                {renderChapterVerses()}
              </div>
            }
            nextPanel={
              chapterNeighborsMeta.next ? (
                neighborChapters.next?.verses?.length ? (
                  <BfChapterPreview verses={neighborChapters.next.verses} />
                ) : (
                  <div className="bf-chapter-swipe-panel__loading" aria-hidden="true" />
                )
              ) : (
                <div className="bf-chapter-swipe-panel__empty" aria-hidden="true" />
              )
            }
          />
        ) : (
          <div
            className="verses-container w-full"
            data-bf-switching={bfCrossfading ? 'true' : 'false'}
          >
            {renderChapterVerses()}
          </div>
        )}
      </div>

      {showBfChapterPager && (
        <BfChapterPager
          prevTarget={prevNavTarget}
          nextTarget={nextNavTarget}
          onNavigate={(url) => {
            if (url === nextNavTarget?.url) navigateChapterBySwipe('next');
            else if (url === prevNavTarget?.url) navigateChapterBySwipe('prev');
            else navigate(url);
          }}
          hidden={bfHeaderState.hidden || bfAmbient.ambient}
        />
      )}

      <div className="chapter-floating-nav">
        <button
          type="button"
          className="chapter-floating-nav-btn chapter-floating-prev"
          disabled={!prevNavTarget}
          onClick={() => prevNavTarget && navigateChapterBySwipe('prev')}
          aria-label={t('previousChapter')}
        >
          ‹
        </button>
        <button
          type="button"
          className="chapter-floating-nav-btn chapter-floating-next"
          disabled={!nextNavTarget}
          onClick={() => nextNavTarget && navigateChapterBySwipe('next')}
          aria-label={t('nextChapter')}
        >
          ›
        </button>
      </div>

      {showVerseImage && (
        <div className="ai-modal" onClick={closeVerseImage} role="dialog" aria-modal="true" aria-label={t('illustration')}>
          <div className="ai-content" onClick={(e) => e.stopPropagation()}>
            <div className="bible-modal-sheet-handle" aria-hidden="true" />
            <div className="ai-header-fixed">
              <div className="flex items-center gap-2 min-w-0">
                <span className="ai-icon flex-shrink-0" aria-hidden="true">🖼️</span>
                <h2 className="text-base font-semibold text-slate-700 truncate">{t('illustration')}</h2>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleCopyVerseImageLink}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                  title={t('copyLink')}
                  aria-label={t('copyLink')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={closeVerseImage}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
                  aria-label={t('close')}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="ai-scrollable-content">
              {verseImageError ? (
                <div className="verse-image-error-block">
                  <p className="verse-image-error">{verseImageError}</p>
                  <button type="button" className="verse-image-error-close" onClick={closeVerseImage}>
                    {t('close')}
                  </button>
                </div>
              ) : verseImageUrl ? (
    <div>
                  <img src={verseImageUrl} alt="" className="verse-image-img" />
                  <p className="verse-image-caption">
                    {bookName} {chapter}:{activeVerse?.verse}
                  </p>
                  {activeVerse?.text && (
                    <p className="verse-image-verse-text">{activeVerse.text}</p>
                  )}
    </div>
              ) : loadingVerseImage ? (
                <BfInlineLoader
                  label={`${t('loading')}…`}
                  hint={canGenerateVerseImage ? t('verseImageMayTake') : undefined}
                  className="bf-inline-loader--verse-image"
                />
              ) : verseImageNotInCache ? (
                <div className="verse-image-empty-block px-4 py-8 text-center">
                  <p className="text-slate-600 font-medium">
                    {t('verseImageNoIllustration')}
                  </p>
                  {activeVerse?.text && (
                    <p className="verse-image-verse-text mt-4 text-left">{activeVerse.text}</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {showParallel && (
        bfEnabled ? (
          <BfParallelVerses
            verses={parallelVerses}
            mainVerse={activeVerse}
            bookName={bookName}
            chapter={chapter}
            translation={translation}
            onClose={closeParallelPlaces}
            onTranslationChange={handleBfTranslationChange}
          />
        ) : (
          <ParallelVerses
            verses={parallelVerses}
            mainVerse={activeVerse}
            bookName={bookName}
            chapter={chapter}
            translation={translation}
            onClose={closeParallelPlaces}
          />
        )
      )}
      
      {showChapterSelector && (
        bfEnabled ? (
          <BfChapterSelector
            bookId={bookId}
            currentChapter={parseInt(chapter)}
            totalChapters={totalChapters}
            bookName={bookName}
            books={books}
            onSelect={(selectedBookId, selectedChapter) => {
              navigate(`/${translation}/${selectedBookId}/${selectedChapter}`, { replace: true });
              setShowChapterSelector(false);
            }}
            onClose={() => setShowChapterSelector(false)}
          />
        ) : (
          <ChapterSelector
            bookId={bookId}
            currentChapter={parseInt(chapter)}
            totalChapters={totalChapters}
            bookName={bookName}
            books={books}
            loginReturnPath={`/${translation}/${bookId}/${chapter}`}
            onSelect={(selectedBookId, selectedChapter) => {
              navigate(`/${translation}/${selectedBookId}/${selectedChapter}`, { replace: true });
              setShowChapterSelector(false);
            }}
            onClose={() => setShowChapterSelector(false)}
          />
        )
      )}
      
      {showHistory && (
        <HistoryModal onClose={() => setShowHistory(false)} />
      )}
      {showSettingsModal && (
        bfEnabled ? (
          <BfReadingSettings
            onClose={() => setShowSettingsModal(false)}
            ambientControl={bfAmbient}
            currentTranslationId={translation}
            onTranslationChange={handleBfTranslationChange}
          />
        ) : (
          <SettingsModal onClose={() => setShowSettingsModal(false)} />
        )
      )}
      {bfReadingSettingsOpen && (
        <BfReadingSettings
          onClose={() => setBfReadingSettingsOpen(false)}
          ambientControl={bfAmbient}
          currentTranslationId={translation}
          onTranslationChange={handleBfTranslationChange}
        />
      )}
      {showLanguagePrompt && (
        <LanguagePromptModal onClose={() => setShowLanguagePrompt(false)} />
      )}

      {showContextSearch && (
        <ContextSearchModal translation={translation} onClose={() => setShowContextSearch(false)} />
      )}

      {showChatPopup && (
        <ChatStreamView
          translation={translation}
          messagePrefix={chatMessagePrefix}
          autoRunVerseExplain={chatAutoRunExplain}
          autoRunMode={chatAutoRunMode}
          initialView={chatInitialView}
          verseSourceBookId={bookId}
          verseSourceChapter={chapter}
          verseSourceVerse={activeVerse?.verse ?? null}
          focusComposer={chatFocusComposer}
          suppressAuthPrompt
          onClose={closeChatPopup}
        />
      )}
      
      {showVerseInsight && activeVerse && (
        <BfVerseInsight
          bookId={bookId}
          chapter={chapter}
          verse={activeVerse.verse}
          reference={`${bookName} ${chapter}:${activeVerse.verse}`}
          verseText={activeVerse.text}
          translation={translation}
          initialTab={verseInsightInitialTab}
          onClose={closeVerseInsight}
        />
      )}

      {showAIInterpretation && activeVerse && (
        bfEnabled ? (
          <BfAIInterpretation
            bookId={bookId}
            chapter={chapter}
            verse={activeVerse.verse}
            translation={translation}
            initialTab={aiInitialTab}
            onClose={closeAIInterpretation}
            onAsk={() => {
              const v = activeVerse;
              const openChat = () => {
                closeAIInterpretation();
                openVerseChatForVerse(v, false, 'chat', null, true);
              };
              if (answersGoToBotChat()) {
                void askInBotChat(v, 'ask', openChat);
                return;
              }
              openChat();
            }}
            askPending={botStudyPending === 'ask'}
          />
        ) : (
          <AIInterpretation
            bookId={bookId}
            chapter={chapter}
            verse={activeVerse.verse}
            onClose={closeAIInterpretation}
          />
        )
      )}

      {showBookmarks && bookmarkModalAllowMove && (
        <BookmarkModal
          bookId={bookId}
          chapter={chapter}
          verse={verse}
          bookName={bookName}
          translation={translation}
          allowMove
          onClose={() => {
            setShowBookmarks(false);
            refreshBookmarks();
          }}
        />
      )}

      {showBookmarks && !bookmarkModalAllowMove && (
        <SavedSheet
          bookId={bookId}
          chapter={chapter}
          bookName={bookName}
          translation={translation}
          onChanged={refreshBookmarks}
          onClose={() => {
            setShowBookmarks(false);
            refreshBookmarks();
          }}
        />
      )}

      {showAddBookmark && (
        <AddBookmarkDialog
          bookId={bookId}
          chapter={chapter}
          verse={addBookmarkVerse}
          bookName={bookName}
          onClose={() => {
            setShowAddBookmark(false);
            setAddBookmarkVerse(null);
          }}
          onBookmarkAdded={refreshBookmarks}
        />
      )}

      <BfUndoToast toast={undoToast} onDismiss={() => setUndoToast(null)} />

      {bfEnabled && bfMenuOpen && (
        <BfMenuSheet
          onClose={() => setBfMenuOpen(false)}
          onSelectChapter={() => {
            setBfMenuOpen(false);
            setShowChapterSelector(true);
          }}
          onOpenSearch={() => {
            setBfMenuOpen(false);
            if (!requireAuthForAi()) return;
            setShowContextSearch(true);
          }}
          onOpenChat={() => {
            setBfMenuOpen(false);
            if (!requireAuthForAi()) return;
            setChatMessagePrefix('');
            setChatAutoRunExplain(false);
            setShowChatPopup(true);
          }}
          onShowHistory={() => {
            setBfMenuOpen(false);
            setShowHistory(true);
          }}
          onShowBookmarks={() => {
            setBfMenuOpen(false);
            setBookmarkModalAllowMove(false);
            setShowBookmarks(true);
          }}
          onGoSettings={() => {
            setBfMenuOpen(false);
            setBfReadingSettingsOpen(true);
          }}
        />
      )}

      {bfEnabled && !bfAmbient.ambient && isReaderTextSelectionEnabled() && (
        <BfSmartHighlight
          containerSelector=".reading-container-books"
          onTextSelectionActive={() => {
            setVerseInlineMoreOpen(false);
            closeVerseSelection();
          }}
          onAskAI={(text) => {
            if (!requireAuthForAi()) return;
            setChatMessagePrefix(text);
            setChatAutoRunExplain(true);
            setShowChatPopup(true);
          }}
          onSave={() => toggleSaveVerse(activeVerse?.verse ?? null)}
          onShare={() => handleShareVerse()}
        />
      )}
    </div>
    </div>
  );
}

